import React, { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/en-gb";
import {
  Divider,
  FormControl,
  Tooltip,
  CircularProgress
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  apiInvestorDetails,
  getDataDictionaryValues
} from "../../utils/api/api";
import {
  BASE_URL,
  DD_TYPE_BANK_TYPE,
  DD_TYPE_DP_NAMES,
  DD_TYPE_INVESTOR_TYPE,
  SNACKBAR_ERROR,
} from "../../utils/constants/constants";
import DrawerLayout from "../../components/layout/DrawerLayout";
import "react-phone-input-2/lib/material.css";
import { formatDateTime } from "../../utils/StringUtil";
import { Info } from "@mui/icons-material";
import DialogLayout from "../../components/layout/DialogLayout";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const InvestorInfo = ({ open, onClose, investor }) => {
  const [investorData, setInvestorData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const [ckycStatus, setCkycStatus] = useState(0);
  const [ckycStatusFromServer, setCkycStatusFromServer] = useState(0);
  const [bankNameList, setBankNameList] = useState([]);
  const [investorTypeList, setInvestorTypeList] = useState([]);
  const userType = process.env.REACT_APP_USER_TYPE;
  const [kycDetails, setKycDetails] = useState(null);
  const [showKycDetails, setShowKycDetails] = useState(null);
  const [kycImageData, setKycImageData] = useState(null);
  const [dpNameList, setDpNameList] = useState([]);

  const toggleKycImage = (imageData) => {
    if (kycImageData) {
      setKycImageData(null);
    } else {
      setKycImageData(imageData);
    }
  };

  const getDataDictionary = () => {
    getDataDictionaryValues()
      .then((data) => {
        const invTypes = data.filter((item) =>
          item.ddType === DD_TYPE_INVESTOR_TYPE
        );
        const bankTypes = data.filter((item) =>
          item.ddType === DD_TYPE_BANK_TYPE
        );
        const dpNames = data.filter((item) =>
          item.ddType === DD_TYPE_DP_NAMES
        );
        setInvestorTypeList(invTypes);
        setBankNameList(bankTypes);
        setDpNameList(dpNames);
      })
      .catch((e) => {
        console.log(e);
        enqueueSnackbar("Error loading reference data", SNACKBAR_ERROR);
      });
  };

  useEffect(() => {
    if (open) {
      getDataDictionary();
    }
  }, [open]);
  useEffect(() => {
    if (open && investor?.invRid) {
      setIsLoading(true);
      apiInvestorDetails({ invRid: investor.invRid })
        .then((resData) => {
          if (resData) {
            setInvestorData(resData);
            setShowKycDetails(false);
            setKycImageData(null);
            setKycDetails(resData.ckycDataObject);
            setCkycStatus(resData?.ckycStatus);
            setCkycStatusFromServer(resData?.ckycStatus);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          enqueueSnackbar(error, SNACKBAR_ERROR);
          setIsLoading(false);
        });
    }
  }, [open, investor?.invRid]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <DrawerLayout
        drawerWidth={"50%"}
        open={open}
        title={investorData?.fullName || "Investor Details"}
        closeAction={onClose}
        positiveAction={{ label: "Close", onClick: onClose }}
      >
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <DialogLayout
              title={''}
              open={kycImageData !== null}
              maxWidth={'xs'}
              closeAction={() => toggleKycImage()}
            >
              <div className="d-flex justify-content-center">
                <img src={`data:image/png;base64, ${kycImageData}`} alt="..." />
              </div>
            </DialogLayout>

            <Stack sx={{ marginBottom: "1rem" }} direction="column" spacing={2}>
              <p className="section-label p-0 m-0">INVESTOR DETAILS</p>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Name
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.fullName !== null && investorData?.fullName !== ""
                      ? investorData?.fullName
                      : "---"}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Investor Code
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.invCode !== null && investorData?.invCode !== ""
                      ? investorData?.invCode
                      : "---"}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Phone Number
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {(investorData?.countryCode ? investorData?.countryCode : "") +
                      (investorData?.mobile ? investorData?.mobile : "") ?? "---"}
                  </p>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Email
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.email !== null && investorData?.email !== ""
                      ? investorData?.email
                      : "---"}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    DOB
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.dobDoi !== null && investorData?.dobDoi !== ""
                      ? formatDateTime(investorData?.dobDoi, false)
                      : "---"}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Address
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.address !== null && investorData?.address !== ""
                      ? investorData?.address
                      : "---"}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    City
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.cityName !== null && investorData?.cityName !== ""
                      ? investorData?.cityName
                      : "---"}
                  </p>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Country
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.countryName !== null && investorData?.countryName !== ""
                      ? investorData?.countryName
                      : "---"}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Investor Type
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.investorType !== null &&
                      investorData?.investorType !== ""
                      ? investorTypeList.find(
                        (item) => item.ddIndex === investorData?.investorType
                      )?.ddValue
                      : "---"}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    STAMP PAPER?
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {(investorData?.stampPaper ? "YES" : "NO") ?? "---"}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Upfront
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.upfront !== null && investorData?.upfront !== ""
                      ? investorData?.upfront
                      : "---"}
                    %
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Class Name
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.className !== null && investorData?.className !== ""
                      ? investorData?.className
                      : "---"}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                {userType === "ADMIN" && (
                  <Box sx={{ flex: 1 }}>
                    <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                      Partner Name
                    </p>
                    <p className="fs-med-medium p-0 m-0">
                      {investorData?.partnerName !== null &&
                        investorData?.partnerName !== ""
                        ? investorData?.partnerName
                        : "---"}
                    </p>
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    USER CREATED DATE
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.createdDatetime !== null && investorData?.createdDatetime !== ""
                      ? formatDateTime(investorData?.createdDatetime, false)
                      : "---"}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    INCL DEMAT CHARGES?
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {(investorData?.includeDematCharge ? "YES" : "NO") ?? "---"}
                  </p>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    LOGIN
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {(investorData?.loginEnabled ? "Enabled" : "Disabled") ?? "---"}
                  </p>
                </Box>
              </Stack>
            </Stack>
            <Divider />
            <Stack
              sx={{ marginBottom: "1rem", marginTop: "1rem" }}
              direction="column"
              spacing={2}
            >
              <p className="section-label p-0 m-0">BANK DETAILS</p>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Bank Name
                  </p>
                  <div className="fs-med-medium p-0 m-0 d-flex flex-row gap-2">
                    <p>
                      {investorData?.bankIndex !== null && investorData?.bankIndex !== ""
                        ? investorData?.bankIndex === 0
                          ? investorData?.otherBankName
                          : bankNameList.find((item) => item.ddIndex === investorData?.bankIndex)?.ddValue
                        : "---"}
                    </p>
                    <p>
                      {investorData?.otherBankName && investorData.bankIndex === 0 ? (
                        <Tooltip title="Investor Selected Other Bank Name" placement="bottom">
                          <Box display="d-flex flex-row " alignItems="center">
                            <b>
                              <InfoOutlinedIcon sx={{ fontSize: '16px', color: 'red', fontWeight: 'bold' }} />
                              <span className="text-danger px-1"> Need Action</span>
                            </b>
                          </Box>
                        </Tooltip>
                      ) : null}
                    </p>
                  </div>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Address
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.bankAddress !== null && investorData?.bankAddress !== ""
                      ? investorData?.bankAddress
                      : "---"}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Account Number
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.accountNo !== null && investorData?.accountNo !== ""
                      ? investorData?.accountNo
                      : "---"}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    IFSC
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.ifscSwift !== null && investorData?.ifscSwift !== ""
                      ? investorData?.ifscSwift
                      : "---"}
                  </p>
                </Box>
              </Stack>
            </Stack>
            <Divider />

            <Stack
              sx={{ marginBottom: "1rem", marginTop: "1rem" }}
              direction="column"
              spacing={2}
            >
              <p className="section-label p-0 m-0">DEMAT DETAILS</p>

              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>Demat Account Number</p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.dematAccNo ?? '---'}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>Depository Participant (DP) Name</p>
                  <div className="fs-med-medium p-0 m-0 d-flex flex-row gap-2">
                    <p>
                      {investorData?.dpDdIndex !== null && investorData?.dpDdIndex !== ""
                        ? investorData?.dpDdIndex === 0
                          ? investorData?.otherDematName
                          : dpNameList.find((item) => item.ddIndex === investorData?.dpDdIndex)?.ddValue
                        : investorData?.dpName ? investorData?.dpName : "---"}
                    </p>
                    <p>
                      {investorData?.otherDematName && investorData.dpDdIndex === 0 ? (
                        <Tooltip title="Investor Selected Other Depository Participant (DP) Name" placement="bottom">
                          <Box display="d-flex flex-row " alignItems="center">
                            <b>
                              <InfoOutlinedIcon sx={{ fontSize: '16px', color: 'red', fontWeight: 'bold' }} />
                              <span className="text-danger px-1"> Need Action</span>
                            </b>
                          </Box>
                        </Tooltip>
                      ) : null}
                    </p>
                  </div>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>DP ID</p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.dpId ?? '---'}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>Client ID</p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.dpClientId ?? '---'}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>First Holder Name</p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.firstHolderName ?? '---'}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>CML COPY</p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.cmlCopyObject ? <Tooltip title={investorData?.cmlCopyObject?.actualFileName}>
                      <AttachFileIcon color="secondary" onClick={() => window.open(`${BASE_URL}/${investorData?.cmlCopyObject?.path}`, '_blank')} />
                    </Tooltip> : '---'}
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>NSDL / CDSL</p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.nsdlCdsl ?? '---'}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>CML COPY UPLOAD DATE</p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.cmlCopyUploadDatetime ? formatDateTime(investorData.cmlCopyUploadDatetime, false) : '---'}
                  </p>
                </Box>
              </Stack>
            </Stack>

            <Divider />
            <Stack
              sx={{ marginBottom: "1rem", marginTop: "1rem" }}
              direction="column"
              spacing={2}
            >
              <p className="section-label p-0 m-0">CKYC</p>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Pan Number
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.pan !== null && investorData?.pan !== ""
                      ? investorData?.pan
                      : "---"}
                  </p>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                    Status
                  </p>
                  <p className="fs-med-medium p-0 m-0">
                    {ckycStatusFromServer ? 'Verified' : 'Not Verified'}
                    {
                      ckycStatusFromServer && kycDetails?.ckycavailable === 'Yes'
                        ? <Info color="primary" className="ms-1" style={{ cursor: 'pointer' }} onClick={() => setShowKycDetails(!showKycDetails)} />
                        : ''
                    }
                  </p>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <p className="section-label mb-1" style={{ fontSize: "12px" }}>GST Number</p>
                  <p className="fs-med-medium p-0 m-0">
                    {investorData?.gstNo ?? '---'}
                  </p>
                </Box>
              </Stack>
              {
                showKycDetails ?
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <div className="d-flex gap-3 align-items-center">
                        <img src={`data:image/png;base64, ${kycDetails?.ckycphoto}`} alt="..." style={{ cursor: "pointer", width: '70px' }} onClick={() => toggleKycImage(kycDetails?.ckycphoto)} />
                        <div className="d-flex flex-column">
                          <p className="section-label mb-1" style={{ fontSize: "12px" }}>Name</p>
                          <p className="fs-med-medium p-0 m-0">{kycDetails?.ckycname ?? "---"}</p>
                        </div>
                      </div>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <p className="section-label mb-1" style={{ fontSize: "12px" }}>Account Type</p>
                      <p className="fs-med-medium p-0 m-0">{kycDetails?.ckycaccType ?? "---"}</p>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <p className="section-label mb-1" style={{ fontSize: "12px" }}>CKYC ID</p>
                      <p className="fs-med-medium p-0 m-0">{kycDetails?.ckycid ?? "---"}</p>
                    </Box>
                  </Stack> : ''
              }
            </Stack>
            <Divider />
            <Stack
              sx={{ marginBottom: "1rem", marginTop: "1rem" }}
              direction="column"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <p className="section-label mb-1" style={{ fontSize: "12px" }}>
                  ACCREDITATION
                </p>
                <p className="fs-med-medium p-0 m-0">
                  {investorData?.isAccredited === 1 ? "Verified" : "Not Verified"}
                </p>
              </Box>
            </Stack>
            {ckycStatus ? (
              <div>
                <Divider />
                <Stack
                  sx={{ marginBottom: "1rem", marginTop: "1rem" }}
                  direction="column"
                  spacing={2}
                >
                  <Stack direction="row" spacing={2}>
                    <FormControl sx={{ flex: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <p
                          className="section-label mb-1"
                          style={{ fontSize: "12px" }}
                        >
                          Investment Preference
                        </p>
                        <p className="fs-med-medium p-0 m-0">
                          {investorData?.invPref === 1
                            ? "Lumpsum Investment"
                            : investorData?.invPref === 2
                              ? "SIP Investment"
                              : "None"}
                        </p>
                      </Box>
                    </FormControl>
                    <Box sx={{ flex: 1 }} />
                  </Stack>
                </Stack>
              </div>
            ) : (
              ""
            )}
          </>
        )}
      </DrawerLayout>
    </LocalizationProvider>
  );
};

export default InvestorInfo;